import { ClearOutlined, MessageOutlined } from '@ant-design/icons';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Flex, Input, Skeleton, Space } from 'antd';
import { useState } from 'react';
import {
  BlankState,
  type CreatedRange,
  CreatedRangeFilter,
  ExportCsvButton,
  FilterRow,
  FilterRowSpacer,
  InlineError,
  PageHeader,
  createdRangeParams,
  hasCreatedRange,
  useTablePagination,
} from '../../../../components';
import { AdminFeedbackDrawer, AdminFeedbackTable } from '../../../../features/admin/direct-feedback';
import { UserSelect } from '../../../../features/admin/users';
import {
  type DirectFeedbackFilters,
  buildDirectFeedbackParams,
  useDirectFeedbackPaged,
} from '../../../../features/direct-feedback';
import { recordNumberParam } from '../../../../lib/search-params';

type Search = {
  feedbackId?: string;
};

const FeedbackPage = () => {
  const { feedbackId } = Route.useSearch();
  const navigate = useNavigate();
  const [author, setAuthor] = useState<string>('');
  const [receiver, setReceiver] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [dateRange, setDateRange] = useState<CreatedRange>(null);

  const filters: DirectFeedbackFilters = createdRangeParams(dateRange);
  if (author) filters.author = author;
  if (receiver) filters.receiver = receiver;
  if (search) filters.q = search;

  const { page, pageSize, buildPagination } = useTablePagination(JSON.stringify(filters));
  const feedbackQuery = useDirectFeedbackPaged(filters, page, pageSize);
  const all = feedbackQuery.data?.feedback ?? [];
  const hasActiveFilters = Boolean(author || receiver || search || hasCreatedRange(dateRange));

  const applySearch = (v: string) => {
    setSearchInput(v);
    setSearch(v.trim());
  };

  const clearFilters = () => {
    setAuthor('');
    setReceiver('');
    setSearch('');
    setSearchInput('');
    setDateRange(null);
  };

  const openFeedback = (id: string) => {
    navigate({ to: '/admin/feedback', search: (prev: Search) => ({ ...prev, feedbackId: id }) });
  };

  const closeFeedback = () => {
    navigate({ to: '/admin/feedback', search: (prev: Search) => ({ ...prev, feedbackId: undefined }) });
  };

  return (
    <Flex vertical gap="large">
      <PageHeader
        title="Direct feedback"
        subtitle="Review the direct feedback members give each other"
        action={
          <Space>
            <ExportCsvButton
              path="/direct-feedback/export"
              params={buildDirectFeedbackParams(filters)}
              fileBaseName="direct-feedback"
            />
            <Link to="/admin/feedback/new">
              <Button type="primary" icon={<MessageOutlined />}>
                Give Direct Feedback
              </Button>
            </Link>
          </Space>
        }
      />

      <FilterRow>
        <Input.Search
          placeholder="Search feedback"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={applySearch}
          allowClear
          style={{ width: 240 }}
        />
        <UserSelect
          value={author}
          onChange={(v) => setAuthor(v ?? '')}
          allowClear
          includeInactive
          placeholder="Filter by author"
          style={{ width: 220 }}
        />
        <UserSelect
          value={receiver}
          onChange={(v) => setReceiver(v ?? '')}
          allowClear
          includeInactive
          placeholder="Filter by recipient"
          style={{ width: 220 }}
        />
        <CreatedRangeFilter value={dateRange} onChange={setDateRange} />
        <FilterRowSpacer />
        {hasActiveFilters ? (
          <Button type="text" size="small" icon={<ClearOutlined />} onClick={clearFilters}>
            Reset filters
          </Button>
        ) : null}
      </FilterRow>

      {feedbackQuery.isError ? (
        <InlineError title="Couldn't load feedback" detail={feedbackQuery.error.message} />
      ) : null}

      {feedbackQuery.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : all.length === 0 && !feedbackQuery.isError ? (
        <BlankState
          title={hasActiveFilters ? 'No feedback matches these filters' : 'No feedback yet'}
          hint={
            hasActiveFilters
              ? 'Try a different author, recipient, date range, or search term — or clear the filters.'
              : 'Direct feedback members send each other will appear here.'
          }
          action={
            hasActiveFilters ? (
              <Button size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />
      ) : all.length === 0 ? null : (
        <AdminFeedbackTable
          feedback={all}
          loading={feedbackQuery.isFetching}
          onOpenFeedback={openFeedback}
          pagination={buildPagination(feedbackQuery.data?.total ?? 0)}
        />
      )}

      <AdminFeedbackDrawer
        feedbackId={feedbackId ?? null}
        onClose={closeFeedback}
        feedback={all}
        onNavigate={openFeedback}
      />
    </Flex>
  );
};

export const Route = createFileRoute('/_auth/admin/feedback/')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    feedbackId: recordNumberParam(search.feedbackId),
  }),
  component: FeedbackPage,
});
